import { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IconButton, TextField, Text } from '@nike/eds';
import ts from '../../passes/vip/i18n';
import { useI18nContext } from '../../../context/language/use-i18n-context';
import { AppConfig } from '../../../utils/constants';
import { VIP, VendorInfo } from '../type';
import { ts as vendorTS } from '../../passes/vendor/i18n';
import { useLanguageContext } from '../../../context/language/context';
import { Languages } from 'models/translations';
import styles from './vendor-vip.module.css';

type Props = {
  onVIPVendorAdd: (values: VIP | VendorInfo) => void;
  vipVendorToEdit: VIP | VendorInfo;
  emailRequired?: boolean;
  onVIPVendorClear: () => void;
  passType: number;
  weChatIdRequired?: boolean;
  isEdit: boolean;
  handleEdit: (values: VIP | VendorInfo) => void;
};

export const VIPVendorInfo: FC<Props> = ({
  onVIPVendorAdd,
  vipVendorToEdit,
  onVIPVendorClear,
  emailRequired,
  passType,
  weChatIdRequired,
  isEdit,
  handleEdit,
}) => {
  const { i18nString } = useI18nContext();
  const [language] = useLanguageContext();

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('')
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    lastName: Yup.string()
      .required('')
      .matches(
        RegExp(AppConfig.regexPatterns.regexPersonNames),
        AppConfig.validationMessages.namesErrorMessage,
      ),
    email: emailRequired
      ? Yup.string().required('').email(AppConfig.validationMessages.validEmailMessage)
      : Yup.string().email(AppConfig.validationMessages.validEmailMessage),
    weChatId: Yup.number().notRequired(),
  });
  const isVipFormEmpty = (vip: VIP | VendorInfo) => {
    return vip.email == '' && emailRequired && vip.firstName == '' && vip.lastName == '';
  };
  const formik = useFormik({
    validateOnMount: true,
    initialValues: vipVendorToEdit,
    enableReinitialize: true,
    onSubmit: () => {
      formik.resetForm();
    },

    validationSchema: validationSchema,
  });
  const handleChange = () => {
    if (isEdit) {
      handleEdit(formik.values);
    }
  };
  useEffect(() => {
    handleChange();
  }, [formik.values]);

  const handleAdd = (values: VIP | VendorInfo, resetForm: any) => {
    onVIPVendorAdd(values);
    resetForm();
    formik.validateForm();
  };

  return (
    <section className="eds-spacing--p-32">
      <Text className="txt_uppercase eds-spacing--my-12 eds-spacing--mx-4" as={'h3'}>
        {passType === AppConfig.passType.vip
          ? i18nString(ts.PASS_MODAL_VIP_INFO_HEADING)
          : i18nString(vendorTS.PASS_MODAL_VENDOR_INFO_HEADING)}
      </Text>
      <form>
        <div className={`eds-flex eds-flex--justify-content-flex-start`}>
          <div className={styles.row}>
            {(passType === AppConfig.passType.vip || language !== Languages.JA) && (
              <TextField
                className="eds-spacing--mr-16"
                id="firstName"
                data-testid="vip_first_name"
                label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                hideLabel={true}
                type="text"
                name="firstName"
                onChange={formik.handleChange}
                value={formik.values.firstName}
                placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                errorMessage={formik.errors.firstName}
                hasErrors={!!formik.errors.firstName}
              />
            )}
            <TextField
              className={`eds-spacing--mr-16 ${styles.mt_16}`}
              id="lastName"
              data-testid="vip_last_name"
              label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
              hideLabel={true}
              type="text"
              name="lastName"
              onChange={formik.handleChange}
              value={formik.values.lastName}
              placeholder={i18nString(ts.PLACEHOLDER_LAST_NAME)}
              errorMessage={formik.errors.lastName}
              hasErrors={!!formik.errors.lastName}
            />
            {passType !== AppConfig.passType.vip && language === Languages.JA && (
              <TextField
                className={`eds-spacing--mr-16 ${styles.mt_16}`}
                id="firstName-jp"
                data-testid="vip_first_name"
                label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                hideLabel={true}
                type="text"
                name="firstName"
                onChange={formik.handleChange}
                value={formik.values.firstName}
                placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                errorMessage={formik.errors.firstName}
                hasErrors={!!formik.errors.firstName}
              />
            )}
            {passType !== AppConfig.passType.vip && weChatIdRequired && (
              <TextField
                className={`eds-spacing--mr-16 ${styles.mt_16}`}
                id="weChatId"
                data-testid="vendor_WechatId"
                label={i18nString(vendorTS.PLACEHOLDER_WE_CHAT_ID)}
                hideLabel={true}
                type="text"
                name="weChatId"
                style={{ width: '50%' }}
                onChange={formik.handleChange}
                value={formik.values.weChatId ?? ''}
                placeholder={i18nString(vendorTS.PLACEHOLDER_WE_CHAT_ID)}
                errorMessage={formik.errors.weChatId}
                hasErrors={!!formik.errors.weChatId}
              />
            )}
            <TextField
              className={`eds-spacing--mr-16 ${styles.mt_16}`}
              id="email"
              data-testid="vip_email"
              label={i18nString(
                emailRequired ? ts.PLACEHOLDER_EMAIL_ADDRESS : ts.PLACEHOLDER_EMAIL_OPTIONAL,
              )}
              hideLabel={true}
              type="text"
              name="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              placeholder={i18nString(
                emailRequired ? ts.PLACEHOLDER_EMAIL_ADDRESS : ts.PLACEHOLDER_EMAIL_OPTIONAL,
              )}
              errorMessage={formik.errors.email}
              hasErrors={!!formik.errors.email}
            />
          </div>
          {!isEdit && (
            <div className="eds-flex eds-flex--justify-content-flex-end eds-flex--direction-row eds-flex--align-items-center">
              <IconButton
                className="eds-spacing--mr-8"
                label="Add VIP"
                aria-label="add VIP pass"
                size="medium"
                icon="Plus"
                data-testid="add_vip"
                disabled={!formik.isValid || isVipFormEmpty(formik.values)}
                onClick={() => handleAdd(formik.values, formik.resetForm)}
                variant="primary"
              ></IconButton>
              <IconButton
                label="Clear"
                aria-label="Clear VIP pass"
                size="medium"
                icon="Close"
                data-testid="clear_vip"
                disabled={
                  !(
                    formik.values.email != '' ||
                    formik.values.firstName != '' ||
                    formik.values.lastName != ''
                  )
                }
                onClick={() => {
                  formik.resetForm();
                  onVIPVendorClear();
                }}
                variant="primary"
              ></IconButton>
            </div>
          )}
        </div>
      </form>
    </section>
  );
};

export default VIPVendorInfo;
